import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, Phone, MessageCircle, Sparkles, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { SITE, telLink, whatsappLink } from "@/config/site";
import { track } from "@/lib/analytics";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services", dropdown: true },
  { label: "Prices", to: "/prices" },
  { label: "How It Works", to: "/#how-it-works" },
  { label: "Service Areas", to: "/#service-areas" },
  { label: "FAQ", to: "/#faq" },
  { label: "Contact", to: "/contact" },
];

const SERVICES_MENU = [
  { label: "Residential Cleaning", to: "/services#residential" },
  { label: "Commercial Cleaning", to: "/commercial-cleaning-dublin" },
  { label: "End of Tenancy Cleaning", to: "/end-of-tenancy-cleaning-dublin" },
  { label: "Deep Cleaning", to: "/deep-cleaning-dublin" },
  { label: "Office Cleaning", to: "/office-cleaning-dublin" },
  { label: "Business Cleaning Contracts", to: "/business-cleaning-contracts-dublin" },
  { label: "View all services", to: "/services" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-slate-200 bg-white/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-700 text-white">
            <Sparkles size={18} />
          </span>
          <span className="font-display text-lg font-extrabold tracking-tight text-slate-900">
            Go<span className="text-emerald-700">Cleaning</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {NAV.map((l) =>
            l.dropdown ? (
              <div key={l.label} className="group relative">
                <Link to={l.to} data-testid="nav-services" className="flex items-center gap-1 text-sm font-semibold text-slate-600 transition-colors hover:text-emerald-700">
                  {l.label} <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                </Link>
                <div className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                    {SERVICES_MENU.map((m) => (
                      <Link key={m.to} to={m.to} data-testid={`nav-drop-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="block rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-emerald-50 hover:text-emerald-700">
                        {m.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : l.to.includes("#") ? (
              <a key={l.label} href={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm font-semibold text-slate-600 transition-colors hover:text-emerald-700">{l.label}</a>
            ) : (
              <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm font-semibold text-slate-600 transition-colors hover:text-emerald-700">{l.label}</Link>
            )
          )}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <a
            href={telLink()}
            onClick={track.phoneClick}
            data-testid="header-call-btn"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-800 transition-all hover:border-emerald-600 hover:text-emerald-700"
          >
            <Phone size={15} /> Call Now
          </a>
          <Link
            to="/contact"
            data-testid="header-quote-btn"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-700 px-5 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-800 hover:shadow-lg hover:shadow-emerald-700/20"
          >
            Get a Quote
          </Link>
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-2 lg:hidden">
          <a
            href={whatsappLink()}
            onClick={() => track.whatsappClick("header-mobile")}
            target="_blank" rel="noreferrer"
            data-testid="header-whatsapp-mobile"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366] text-white"
            aria-label="WhatsApp us"
          >
            <MessageCircle size={18} />
          </a>
          <a
            href={telLink()}
            onClick={track.phoneClick}
            data-testid="header-call-mobile"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-700 text-white"
            aria-label="Call now"
          >
            <Phone size={18} />
          </a>
          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                aria-label="Open menu"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-800"
              >
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="mt-8 flex flex-col gap-1">
                {NAV.filter((l) => !l.dropdown).map((l) => (
                  <SheetClose asChild key={l.label}>
                    {l.to.includes("#") ? (
                      <a href={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="rounded-xl px-4 py-3 text-base font-bold text-slate-800 transition-colors hover:bg-emerald-50 hover:text-emerald-700">{l.label}</a>
                    ) : (
                      <Link to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="rounded-xl px-4 py-3 text-base font-bold text-slate-800 transition-colors hover:bg-emerald-50 hover:text-emerald-700">{l.label}</Link>
                    )}
                  </SheetClose>
                ))}

                <p className="mt-3 px-4 text-xs font-bold uppercase tracking-wide text-emerald-700">Services</p>
                {SERVICES_MENU.map((m) => (
                  <SheetClose asChild key={m.to}>
                    <Link to={m.to} data-testid={`mobile-drop-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700">{m.label}</Link>
                  </SheetClose>
                ))}

                <SheetClose asChild>
                  <Link to="/contact" data-testid="mobile-quote-btn" className="mt-4 rounded-full bg-emerald-700 px-5 py-3.5 text-center text-base font-bold text-white">
                    Get a Free Quote
                  </Link>
                </SheetClose>
                <a
                  href={whatsappLink()}
                  onClick={() => track.whatsappClick("mobile-menu")}
                  target="_blank" rel="noreferrer"
                  data-testid="mobile-whatsapp-btn"
                  className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3.5 text-base font-bold text-white"
                >
                  <MessageCircle size={18} /> WhatsApp Us
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
